/**
 * 新潟県の料理12品。
 *
 * 雪国の保存食と、米の県らしい飯ものを軸にした。
 * 上越から笹寿司ではなく笹団子、中越から車麩・栃尾あぶらげ・しょうゆおこわ・へぎそば、
 * 下越から鮭の焼漬け・のっぺ、佐渡からいごねり、魚沼からきりざい。
 *
 * へぎそばは入れてよいと判断した。
 *  そばはすでに6県あるが、農水省のページに「つなぎに乾燥布海苔」「へぎは剥ぎ板で作った
 *  四角い器」「一口ずつ美しく盛った」「手振り・手びれと呼ばれる」と書かれている。
 *  四角い木の板に麺の束が整列する絵は、どの県のそばとも重ならない。
 *
 * 笹団子も入れてよいと判断した。
 *  島根の笹巻きは、あんもよもぎも入らない小判型のだんごを芯棒に刺して包むもの。
 *  新潟の笹団子はよもぎを練り込んだ緑の生地に小豆あんを包んだ丸い団子を、
 *  笹3枚で俵形に包んでスゲかイグサで両端をくくる。仕様でこの違いを固定した。
 *  三角ちまきは笹ものが二重になるので入れていない。
 *
 * 他県と重ならないように外したもの。
 *  - くじら汁 … 作品全体で鯨料理は扱わない
 *  - タレかつ丼 … 福井のソースカツ丼と同じ「丼にカツ」の絵になる
 *  - 笹寿司 … 笹団子と笹が二重になる
 *  - ラーメン・そうめん・和牛 … すでに他県に多い
 *  - 日本酒 … 作品の方針で酒類は料理に入れない
 *
 * 出典に見た目が書かれていない品（きりざい・煮菜など）は、
 * 材料と作り方から確実に言えることだけを仕様に書いている。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：ふかしなす・車麩の煮物・かきあえなます・いごねり・しょうゆおこわ（店で食べられる品に差し替え）
 * - 燕三条背脂ラーメン：発祥の店名は書かない（店の宣伝になる。燕市観光協会は『地元ラーメン店で考案された』とだけ書いている）
 * - 燕三条背脂ラーメン：刻み玉ねぎ・岩のり・チャーシューは、燕市観光協会・燕市・新潟県観光協会のスポットページの料理説明に出てこないので書かない（玉ねぎは新潟県観光協会ブログの1店の紹介にのみ出てくる）
 * - 燕三条背脂ラーメン：『新潟五大ラーメン』は今回の source に書かれていないので description には入れない
 * - 長岡生姜醤油ラーメン：元祖とされる店名・創業年は、自治体の資料で確かめられないので書かない
 * - 長岡生姜醤油ラーメン：スープのだし（豚骨・煮干しなど）や具は、公式資料に書かれていないので書かない
 * - わっぱ飯：発祥の店名・考案者・誕生年（昭和27年）は、新潟市観光ガイド・新潟県観光協会の店紹介記事にはあるが、国や自治体の資料で確かめられないので書かない
 * - わっぱ飯：北大路魯山人の助言の話も、店紹介記事にしか出てこないので書かない
 * - 新潟タレカツ丼：発祥の店名・初代店主の名は、新潟市観光ガイドにはあるが、店の宣伝になるので書かない（農林水産省は『老舗の飲食店』とだけ書いている）
 * - 新潟タレカツ丼：『タレかつ丼』という呼び名ができた年は公式資料で確かめていないので書かない
 * - 村上牛：『日本一』（平成8年度・15年度の受賞を『美味しい牛肉日本一』とする表現）は、村上市のPDFや検索要約には出るが、開いて確かめた村上市のページでは『最高位の名誉賞』という書き方なので、称号としては書かない
 * - 村上牛：村上牛の丼という料理の形は公式資料にないので書かない
  * - 2026-10-12 地元民レビューで「けんさん焼き」を外し「イタリアン」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { niigataSceneryTour } from './niigataScenes';
import { NIIGATA_FOOD_HULLS } from './niigataFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'kirizai',
    level: 1,
    name: 'きりざい',
    shortName: 'きりざい',
    image: '/assets/foods/niigata/01_kirizai.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '刻んだ菜と納豆を、混ぜるだけ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kirizai_niigata.html',
    icon: { label: 'きりざい', shape: 'roe', baseColor: '#8a7a48', accentColor: '#3a5a28' },
    reward: {
      unlockScore: 200,
      area: '南魚沼市・魚沼市',
      headline: '野沢菜漬けと大根を細かく刻み、食べる直前に納豆と混ぜ合わせる魚沼の常備菜。',
      tip: '戦国時代には武士が兵糧として持ち歩いていたという記録が残っている。雪深い地域で、長期間保存が効く漬物や干物などの保存食文化が根付いた。大根を細かく切って塩もみし、野沢菜漬けも細かく刻んで絞り、混ぜてごまを加え、食べる直前に納豆を加えて混ぜる。特に時期はなく、年間を通して食べられている。',
      links: searchLinks('南魚沼市', 'きりざい'),
    },
  },
  {
    id: 'niigata-italian',
    level: 2,
    name: 'イタリアン',
    shortName: 'イタリアン',
    image: '/assets/foods/niigata/02_niigata_italian.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '新潟で「イタリアン」といえば、イタリア料理ではなくご当地の軽食のこと。焼きそばにトマトソースをかけ、白しょうがを添える。県民のソウルフードと紹介されている。',
    source:
      'https://niigata-kankou.or.jp/feature/italian',
    icon: { label: 'イタリアン', shape: 'noodle-bowl', baseColor: '#d8b878', accentColor: '#c0392b' },
    reward: {
      unlockScore: 500,
      area: '新潟市・長岡市ほか',
      headline: '焼きそばにトマトソース',
      tip: '麺は焼きそばとうどんの中間ほどの太さ。県内では2つのチェーン店で食べられ、どちらも長く親しまれている。',
      links: searchLinks('新潟市', 'イタリアン 焼きそば'),
    },
  },
  {
    id: 'sasa-dango',
    level: 3,
    name: '笹団子',
    shortName: '笹団子',
    image: '/assets/foods/niigata/03_sasa_dango.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'よもぎの団子を、笹三枚で俵に包む。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sasa_dango_niigata.html',
    icon: { label: '笹団子', shape: 'dumpling', baseColor: '#3a5a2a', accentColor: '#5a7a3a' },
    reward: {
      unlockScore: 1000,
      area: '新潟市・長岡市',
      headline: 'よもぎを練り込んだ緑の団子で小豆あんを包み、笹三枚で俵形に包んでスゲで両端を縛ったもの。',
      tip: 'だんご粉に水でもどしたよもぎを加えてよくこね、生地に小豆あんをつつんで丸い団子をつくる。笹の葉を三枚合わせた上に団子をのせて包み、スゲかイグサで縛り、蒸器で二十分蒸す。笹には殺菌作用があることから戦国時代の携行保存食とされ、上杉謙信が携帯食にしていたといわれる。五月五日の端午の節句（だんご節句）の行事食。昭和39年の新潟国体で土産として推薦されたことがきっかけで一躍有名になった。',
      links: searchLinks('新潟市', '笹団子'),
    },
  },
  {
    id: 'tochio-aburage',
    level: 4,
    name: '栃尾あぶらげ',
    shortName: '栃尾あぶらげ',
    image: '/assets/foods/niigata/04_tochio_aburage.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '長さ20センチ、厚さ3センチ。',
    source:
      'https://www.city.nagaoka.niigata.jp/kankou/aji/ajiwau/aburage.html',
    icon: { label: '栃尾あぶらげ', shape: 'dumpling', baseColor: '#d8a858', accentColor: '#e8e4d8' },
    reward: {
      unlockScore: 1800,
      area: '長岡市栃尾',
      headline: '長さ20から22センチ、厚さ3センチもある大きな油揚げを、直火で焼いて薬味と食べる。',
      tip: 'サイズは長さが20から22センチ、幅が6から8センチ、厚さが3センチくらい。中の芯までふっくら揚げるために、低温と高温の二つの鍋で二度揚げをする。宝暦8年、約260年前に秋葉神社の神官から依頼されて江戸で修行した豆腐職人の林蔵が創案したという説と、江戸中期の栃尾の馬市で考案されたという説がある。直火で焼き、薬味としょうがと大根おろしとからしを添えてつけしょうゆで食べる。',
      links: searchLinks('長岡市', '栃尾あぶらげ'),
    },
  },
  {
    id: 'noppe',
    level: 5,
    name: 'のっぺ',
    shortName: 'のっぺ',
    image: '/assets/foods/niigata/05_noppe.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '里芋のとろみが、冷めても残る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/noppe_niigata.html',
    icon: { label: 'のっぺ', shape: 'rice-bowl', baseColor: '#d8c8a0', accentColor: '#c84040' },
    reward: {
      unlockScore: 3000,
      area: '新潟県内全域',
      headline: '里芋を主材料に、ホタテ貝柱やしいたけ、鮭を薄味で煮てとろみをつけた新潟の代表的な家庭料理。',
      tip: '里芋を主材料とし、野菜やきのこなどを薄味で煮たもの。新潟は雪深い土地のため、買い物もままならない日にたくさんつくり、雪を冷蔵庫代わりにして鍋ごと雪の中で保存していた。汁物というより煮物で、とろみがついているのが特徴。青味にはさやえんどうが使われる。正月料理の定番だが一年を通して食べられ、祭りや法事、婚礼など振る舞いのあるときに作られる。',
      links: searchLinks('新潟市', 'のっぺ'),
    },
  },
  {
    id: 'tsubame-seabura-ramen',
    level: 6,
    name: '燕三条背脂ラーメン',
    shortName: '燕背脂ラーメン',
    image: '/assets/foods/niigata/06_tsubame_seabura_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '伸びにくい極太麺に、煮干しなど魚介の出汁が効いたやや濃い醤油味のスープ。丼から湯気が上がらないほど、たっぷりの豚の背脂をのせる。背脂がスープを覆うので冷めにくい。',
    source:
      'https://tsubame-kankou.jp/ramen/',
    icon: { label: '背脂ラーメン', shape: 'ramen', baseColor: '#5a3a22', accentColor: '#f4efe4' },
    reward: {
      unlockScore: 4500,
      area: '燕市',
      headline: '湯気が立たないほどの背脂',
      tip: '昭和初期、金属洋食器づくりで夜遅くまで働く燕の職人は、出前をすぐに食べられないことが多かった。そこで冷めにくく伸びにくい背脂ラーメンが考え出されたという。',
      links: searchLinks('燕市', '燕背脂ラーメン'),
      official: [{ label: '燕市「燕の背脂ラーメンが『100年フード』に認定されました。」', url: 'https://www.city.tsubame.niigata.jp/soshiki/sangyo_shinko/1/70/12242.html' }, { label: 'にいがた観光ナビ（新潟県観光協会）「燕のソウルフード『燕背脂ラーメン』」', url: 'https://niigata-kankou.or.jp/spot/detail_12376.html' }],
    },
  },
  {
    id: 'nagaoka-shoga-shoyu-ramen',
    level: 7,
    name: '長岡生姜醤油ラーメン',
    shortName: '生姜醤油ラーメン',
    image: '/assets/foods/niigata/07_nagaoka_shoga_shoyu_ramen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '醤油味のスープに生姜の風味をきかせたラーメン。ラーメン店が多く集まる長岡で古くから根付いてきた味で、寒い日には体が温まる。近年は東京にも店が出ている。',
    source:
      'https://www.city.nagaoka.niigata.jp/shisei/cate02/youran/pickup.html',
    icon: { label: '生姜醤油', shape: 'ramen', baseColor: '#3a2414', accentColor: '#e8c86a' },
    reward: {
      unlockScore: 6500,
      area: '長岡市',
      headline: '生姜が香る濃い色の醤油スープ',
      tip: '生姜がふわりと香る濃い色のスープが目印。新潟県観光協会は、燕背脂・新潟濃厚味噌・新潟あっさり醤油・三条カレーと並ぶ「新潟5大ラーメン」のひとつに挙げている。',
      links: searchLinks('長岡市', '長岡生姜醤油ラーメン'),
      official: [{ label: 'にいがた観光ナビ（新潟県観光協会）「新潟５大ラーメン」を食す！【①長岡生姜醤油ラーメン 編】', url: 'https://niigata-kankou.or.jp/blog/detail_485.html' }],
    },
  },
  {
    id: 'wappa-meshi',
    level: 8,
    name: 'わっぱ飯',
    shortName: 'わっぱ飯',
    image: '/assets/foods/niigata/08_wappa_meshi.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '杉の曲げわっぱにご飯を入れ、鮭やいくらなど新潟でとれる海の幸をのせて一気に蒸し上げる。わっぱごと蒸すので、ご飯と具のうまみを逃さず魚介の臭みも消える。杉の香りがご飯に移る。',
    source:
      'https://www.nvcb.or.jp/feature/inakaya',
    icon: { label: 'わっぱ飯', shape: 'rice-bowl', baseColor: '#d9b98a', accentColor: '#f08a4b' },
    reward: {
      unlockScore: 9000,
      area: '新潟市',
      headline: '杉のわっぱごと蒸すご飯',
      tip: 'わっぱは、もとは薄い杉板で作った昔の弁当箱。薄味の出汁で炊いたご飯に具をのせ、炊き込まずに短時間で蒸し上げるので、具それぞれの風味が残る。',
      links: searchLinks('新潟市', 'わっぱ飯'),
    },
  },
  {
    id: 'niigata-tare-katsudon',
    level: 9,
    name: '新潟タレカツ丼',
    shortName: 'タレカツ丼',
    image: '/assets/foods/niigata/09_niigata_tare_katsudon.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '薄くそぎ切りにした豚肉に細かいパン粉を薄くまぶして揚げ、揚げたてを醤油にみりんや砂糖を加えた甘めのたれにくぐらせてご飯にのせる。卵でとじず、1つの丼にカツを2枚以上盛る。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tare_katsudon_niigata.html',
    icon: { label: 'タレカツ丼', shape: 'rice-bowl', baseColor: '#6b3a1a', accentColor: '#f3ead8' },
    reward: {
      unlockScore: 12000,
      area: '新潟市',
      headline: '卵でとじない甘辛だれのカツ',
      tip: '昭和初期、新潟市の西堀の屋台にいた飲食店が、カツレツを醤油だれにくぐらせてご飯にのせたのが始まりとされる。修業した弟子たちが独立し、新潟市のご当地グルメとして定着した。',
      links: searchLinks('新潟市', 'タレカツ丼'),
      official: [{ label: '新潟市観光ガイド「新潟市の王道グルメ完全ガイド 地元民に愛される名物6選」', url: 'https://www.nvcb.or.jp/feature/royalroad_gourmet' }],
    },
  },
  {
    id: 'sake-yakizuke',
    level: 10,
    name: '鮭の焼漬け',
    shortName: '鮭の焼漬け',
    image: '/assets/foods/niigata/10_sake_yakizuke.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '白焼きにして、熱いうちに漬ける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/sakeno_yakizuke_niigata.html',
    icon: { label: '鮭の焼漬け', shape: 'fish', baseColor: '#c86038', accentColor: '#3a2418' },
    reward: {
      unlockScore: 16000,
      area: '村上市',
      headline: '生鮭を白焼きにし、熱いうちに酒とみりんと醤油のたれに漬け込んだ村上の保存食。',
      tip: '冷凍技術が発達していなかった頃の保存食として江戸時代からつくられていた。小鍋に酒とみりんを入れて煮切り、醤油を加えて冷ましたたれに、白焼きにしたサケを熱いうちに漬ける。冷めても固くならずふんわりしている。秋の短い期間に出回る生サケを使い、サケ漁は十月から十一月にかけて行われる。村上市は鮭のまちで、百種類以上の料理法があるといわれる。',
      links: searchLinks('村上市', '鮭の焼漬け'),
    },
  },
  {
    id: 'hegisoba',
    level: 11,
    name: 'へぎそば',
    shortName: 'へぎそば',
    image: '/assets/foods/niigata/11_hegisoba.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '一口ずつたたんで、剥ぎ板に並べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/hegisoba_niigata_niigata.html',
    icon: { label: 'へぎそば', shape: 'noodle-bowl', baseColor: '#5a5a48', accentColor: '#e8e4d8' },
    reward: {
      unlockScore: 22000,
      area: '十日町市・小千谷市',
      headline: '布海苔をつなぎにしたそばを、一口ずつたたんで四角い剥ぎ板に整列させて出す魚沼の盛り方。',
      tip: '新潟県の織物文化とそばの食文化が融合して生まれた、魚沼地方発祥といわれる郷土料理。つなぎに乾燥布海苔を使う。もともと織物の緯糸を張るために布海苔が使われ、身近にあるこの布海苔でそばがつくれないかと考えたのがきっかけだったといわれる。「へぎ」は剥ぎ板で作った四角い器で、そこに一口ずつ美しく盛る。この盛り方は「手振り・手びれ」と呼ばれ、織物の糸を撚り紡ぐ手ぐりの動作を表している。',
      links: searchLinks('十日町市', 'へぎそば'),
    },
  },
  {
    id: 'murakami-gyu',
    level: 12,
    name: '村上牛',
    shortName: '村上牛',
    image: '/assets/foods/niigata/12_murakami_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '村上の豊かな自然の中で、コシヒカリの稲わらと乾草をたっぷり与えて約2年かけて育てる黒毛和牛。肉は色あざやかで風味がよく、とくにサーロインのステーキが評判とされる。',
    source:
      'https://www.city.murakami.lg.jp/site/kanko/tokusan-murakamigyu.html',
    icon: { label: '村上牛', shape: 'rice-bowl', baseColor: '#b8484a', accentColor: '#f2d6c8' },
    reward: {
      unlockScore: 30000,
      area: '村上市',
      headline: '稲わらで育てる村上の和牛',
      tip: '村上市・関川村・胎内市で飼育され、格付等級A-4・B-4以上になった黒毛和牛だけが村上牛と呼ばれる。サシ（霜降り）が多く、希少なため幻ともいわれる。',
      links: searchLinks('村上市', '村上牛'),
      official: [{ label: '村上市観光協会「村上牛」', url: 'https://www.sake3.com/gourmet/beef' }, { label: 'JA全農にいがた「にいがた和牛詳細情報と生産者紹介」', url: 'https://www.zennoh.or.jp/nt/product/ssi/meat/wagyu.html' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const niigataFoodStage: StageDefinition = {
  regionId: 'niigata',
  regionName: '新潟県',
  mode: 'food',
  title: '新潟めしマージ',
  theme: {
    primaryColor: '#2a5a7a',
    secondaryColor: '#c8a848',
    accentColor: '#e8d088',
    backgroundColor: '#0e1418',
  },
  /** 背景ツアー「新潟めぐり」。軸は一日の時間。朝の星峠の棚田から、日没の萬代橋へ。 */
  scenery: {
    tour: niigataSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, NIIGATA_FOOD_HULLS),
};
